import axios from 'axios';
import { apiClient, getErrorMessage } from './api';
import { clearStoredActiveTenantId } from './tenantStorage';
import type {
  User,
  OtpRequestPayload,
  OtpVerifyPayload,
  EmailOtpRequestPayload,
  EmailOtpVerifyPayload,
  OtpRequestResponse,
  LoginResponse,
} from '../types';

// ============================================
// Consumer Auth (Phone OTP via BFF)
// ============================================

// Response types from the backend
interface WebAuthApiResponse {
  success: boolean;
  user?: {
    id: number;
    cognitoSub: string;
    phoneNumber: string;
    displayName: string;
    avatarUrl?: string;
  };
  sessionToken?: string;
  expiresIn?: number;
  devOtp?: string;
  error?: string;
  errorCode?: string;
  retryAfterSeconds?: number;
}

interface WebSessionStateApiResponse {
  userId: string;
  cognitoSub?: string;
  email?: string;
  mobileNumber?: string;
  firstName?: string;
  lastName?: string;
  profileName?: string;
  profilePictureUrl?: string;
  ageVerified: boolean;
  ageEligibilityStatus?: User['ageEligibilityStatus'];
  adultAttestationCurrent?: boolean;
  adultAttestationVersion?: string;
  adultAttestedAt?: string;
  consumerAccessState?: string;
  onboardingComplete: boolean;
  emailVerified: boolean;
  mobileVerified: boolean;
  accountMode?: string;
  nextStep?: User['nextStep'];
  missingFields?: string[];
}

function mapSessionStateToUser(data: WebSessionStateApiResponse): User {
  const displayName =
    data.profileName ||
    [data.firstName, data.lastName].filter(Boolean).join(' ').trim() ||
    null;

  return {
    id: Number(data.userId),
    cognitoSub: data.cognitoSub,
    phone: data.mobileNumber || '',
    displayName,
    avatarUrl: data.profilePictureUrl || null,
    profileName: data.profileName || null,
    firstName: data.firstName || null,
    lastName: data.lastName || null,
    email: data.email || null,
    ageVerified: data.ageVerified,
    ageEligibilityStatus: data.ageEligibilityStatus,
    adultAttestationCurrent: data.adultAttestationCurrent,
    adultAttestationVersion: data.adultAttestationVersion || null,
    adultAttestedAt: data.adultAttestedAt || null,
    consumerAccessState: data.consumerAccessState || null,
    onboardingComplete: data.onboardingComplete,
    emailVerified: data.emailVerified,
    mobileVerified: data.mobileVerified,
    accountMode: data.accountMode || null,
    nextStep: data.nextStep,
    missingFields: data.missingFields || [],
  };
}

export class OtpChallengeError extends Error {
  constructor(message: string, public code?: string, public sessionToken?: string,
    public expiresIn?: number, public retryAfterSeconds?: number) {
    super(message);
    this.name = 'OtpChallengeError';
  }
}

function getWebAuthError(error: unknown, fallback: string): Error {
  if (error instanceof OtpChallengeError) return error;
  if (axios.isAxiosError<WebAuthApiResponse>(error)) {
    const data = error.response?.data;
    const code = data?.errorCode || (error.response?.status === 429 ? 'RATE_LIMITED' : undefined);
    const retryAfter = data?.retryAfterSeconds ?? Number(error.response?.headers?.['retry-after']);
    return new OtpChallengeError(data?.error || fallback, code, data?.sessionToken,
      data?.expiresIn, Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined);
  }

  return new Error(getErrorMessage(error) || fallback);
}

/**
 * Request OTP for phone number
 * Calls Spring Boot BFF which calls Cognito InitiateAuth
 * Returns a sessionToken needed for verification
 */
export async function requestOtp(payload: OtpRequestPayload): Promise<OtpRequestResponse> {
  try {
    // Use apiClient directly - backend returns response directly, not wrapped
    const response = await apiClient.post<WebAuthApiResponse>('/auth/web/start-login', payload);
    const data = response.data;

    if (!data.success || !data.sessionToken) {
      throw new Error(data.error || 'Failed to send OTP');
    }

    return {
      sessionToken: data.sessionToken,
      expiresIn: data.expiresIn ?? 300,
      retryAfterSeconds: data.retryAfterSeconds ?? 60,
    };
  } catch (error) {
    throw getWebAuthError(error, 'Failed to send OTP');
  }
}

export async function requestEmailOtp(payload: EmailOtpRequestPayload): Promise<OtpRequestResponse> {
  try {
    const response = await apiClient.post<WebAuthApiResponse>('/auth/web/start-email-login', payload);
    const data = response.data;

    if (!data.success || !data.sessionToken) {
      throw new Error(data.error || 'Failed to send OTP');
    }

    return {
      sessionToken: data.sessionToken,
      expiresIn: data.expiresIn ?? 300,
      retryAfterSeconds: data.retryAfterSeconds ?? 60,
    };
  } catch (error) {
    throw getWebAuthError(error, 'Failed to send OTP');
  }
}

/**
 * Verify OTP and establish session
 * Calls Spring Boot BFF which calls Cognito RespondToAuthChallenge
 * On success, server sets httpOnly session cookie automatically
 */
export async function verifyOtp(payload: OtpVerifyPayload): Promise<LoginResponse> {
  try {
    // Use apiClient directly - backend returns response directly, not wrapped
    const response = await apiClient.post<WebAuthApiResponse>('/auth/web/verify-otp', payload);
    const data = response.data;

    if (!data.success || !data.user) {
      throw new Error(data.error || 'Failed to verify OTP');
    }

    return {
      user: await getCurrentUser(),
    };
  } catch (error) {
    throw getWebAuthError(error, 'Failed to verify OTP');
  }
}

export async function verifyEmailOtp(payload: EmailOtpVerifyPayload): Promise<LoginResponse> {
  try {
    const response = await apiClient.post<WebAuthApiResponse>('/auth/web/verify-email-otp', payload);
    const data = response.data;

    if (!data.success || !data.user) {
      throw new Error(data.error || 'Failed to verify OTP');
    }

    return { user: await getCurrentUser() };
  } catch (error) {
    throw getWebAuthError(error, 'Failed to verify OTP');
  }
}

/**
 * Get current authenticated user
 * Uses session cookie automatically
 */
export async function getCurrentUser(): Promise<User> {
  const response = await apiClient.get<WebSessionStateApiResponse>('/api/v1/web/session');
  return mapSessionStateToUser(response.data);
}

/**
 * Check if user is authenticated
 * Returns user if authenticated, null otherwise
 */
export async function checkAuth(): Promise<User | null> {
  try {
    return await getCurrentUser();
  } catch {
    clearStoredActiveTenantId();
    return null;
  }
}

/**
 * Logout - clears session cookie on server
 */
export async function logout(): Promise<void> {
  await apiClient.post('/auth/web/logout');
  clearStoredActiveTenantId();
}
